import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";
import { ArrowLeft } from "lucide-react";
import { USDollar } from "~/functions";
import { format, parseISO } from "date-fns";
import useReportsStore from "~/store/reportsStore";

const COLORS = {
  base: {
    amount: "#EE5827",
    discount: "rgba(238, 88, 39, 0.5)",
    fees: "#27306c80",
  },
  compare: {
    amount: "#0EA5E9",
    discount: "#BAE6FD",
    fees: "#94A3B8",
  },
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return parseISO(value);
  return null;
};

const formatMonthLabel = (range, isYearOnly) => {
  if (!range?.from) return "";
  try {
    const date = toDate(range.from);
    if (!date) return "";
    return format(date, isYearOnly ? "yyyy" : "MMM yyyy");
  } catch (error) {
    return "";
  }
};

const CustomTooltip = ({ active, payload, isYearVsYear }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const formatRange = (range) => {
    if (!range?.from || !range?.to) return "Period";
    try {
      const fromDate = toDate(range.from);
      const toDateValue = toDate(range.to);
      if (!fromDate || !toDateValue) return "Period";
      const fromLabel = format(fromDate, "MMM dd, yyyy");
      const toLabel = format(toDateValue, "MMM dd, yyyy");
      return `${fromLabel} - ${toLabel}`;
    } catch (error) {
      return "Period";
    }
  };
  const baseLabel = formatMonthLabel(d.period, isYearVsYear) || "Current";
  const compareLabel = formatMonthLabel(d.compareMonth, isYearVsYear) || "Compare";

  return (
    <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
      <p className="mb-1 font-semibold">{d.name}</p>
      <div className="text-sm space-y-1">
        <div className="text-xs text-gray-500">{formatRange(d.period)}</div>
        <p><span className="font-medium">Total ({baseLabel}):</span> {USDollar.format(d.total)}</p>
        <p className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.base.amount }}></span>
          <span className="font-medium">Amount:</span> {USDollar.format(d.amount)}
        </p>
        <p className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.base.discount }}></span>
          <span className="font-medium">Discount:</span> {USDollar.format(d.discount)}
        </p>
        <p className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.base.fees }}></span>
          <span className="font-medium">Fees:</span> {USDollar.format(d.fees)}
        </p>
        {d.compareMonth && (
          <div className="border-t border-gray-200 mt-2 pt-2">
            <p className="text-xs font-semibold text-gray-500 mb-1">Compare Period</p>
            <div className="text-xs text-gray-500">{formatRange(d.compareMonth)}</div>
            <p><span className="font-medium">Total ({compareLabel}):</span> {USDollar.format(d.compareMonth.total || 0)}</p>
            <p className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.compare.amount }}></span>
              <span className="font-medium">Amount:</span> {USDollar.format(d.compareMonth.amount || 0)}
            </p>
            <p className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.compare.discount }}></span>
              <span className="font-medium">Discount:</span> {USDollar.format(d.compareMonth.discount || 0)}
            </p>
            <p className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.compare.fees }}></span>
              <span className="font-medium">Fees:</span> {USDollar.format(d.compareMonth.fees || 0)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function FilialBreakdownChart({ data, onClose }) {
  if (!data) return null;
  const { filters } = useReportsStore();
  const period = filters.periodsByReport?.GrossValues || filters.period;
  const isYearVsYear = period?.label === "Year vs Year";
  const basePeriod = period
    ? { from: period.from, to: period.to }
    : null;
  const comparePeriod = period
    ? { from: period.compare_from, to: period.compare_to }
    : null;
  const hasCompare = data.some((item) => item.compareMonth);
  const chartData = data.map((item) => {
    const rawCompare = item.compareMonth || {};
    const compareMonth =
      hasCompare && (rawCompare?.from && rawCompare?.to)
        ? rawCompare
        : hasCompare
        ? { ...rawCompare, ...comparePeriod }
        : undefined;
    return {
      ...item,
      period: item.period || basePeriod,
      compareMonth,
      totalCurrent: item.total || 0,
      totalCompare: compareMonth?.total || 0,
      compareAmount: compareMonth?.amount || 0,
      compareDiscount: compareMonth?.discount || 0,
      compareFees: compareMonth?.fees || 0,
      currentPeriodLabel: formatMonthLabel(item.period || basePeriod, isYearVsYear),
      comparePeriodLabel: hasCompare ? formatMonthLabel(compareMonth, isYearVsYear) : "",
    };
  });

  return (
    <div className="w-full p-4 border rounded-lg mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-lg font-bold">Invoices by Filial</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#EE5827] rounded-sm"></div>
            <span className="text-sm">Amount</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(238, 88, 39, 0.5)" }}></div>
            <span className="text-sm">Discount</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary/50 rounded-sm"></div>
            <span className="text-sm">Fees</span>
          </div>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          No data available for this period.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={440}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 10, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.15} />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={90}
            fontSize={12}
            interval={0}
          />
          <YAxis
            tickFormatter={(value) => USDollar.format(value)}
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip isYearVsYear={isYearVsYear} />} cursor={{ fill: "transparent" }} />
          <Bar
            dataKey="amount"
            fill={COLORS.base.amount}
            radius={[0, 0, 0, 0]}
            stackId="amount"
          >
            {hasCompare && (
              <LabelList
                dataKey="currentPeriodLabel"
                position="bottom"
                fontSize={10}
                fill="#6B7280"
              />
            )}
          </Bar>
          <Bar
            dataKey="discount"
            fill={COLORS.base.discount}
            radius={[8, 8, 0, 0]}
            stackId="amount"
          />
          <Bar
            dataKey="fees"
            fill={COLORS.base.fees}
            radius={[8, 8, 0, 0]}
            stackId="amount"
          >
            <LabelList
              dataKey="totalCurrent"
              position="top"
              formatter={(value) => USDollar.format(value)}
              fontSize={10}
              fill="#111827"
            />
          </Bar>
          {hasCompare && (
            <>
              <Bar
                dataKey="compareAmount"
                fill={COLORS.compare.amount}
                radius={[0, 0, 0, 0]}
                stackId="compare"
              >
                <LabelList
                  dataKey="comparePeriodLabel"
                  position="bottom"
                  fontSize={10}
                  fill="#6B7280"
                />
              </Bar>
              <Bar
                dataKey="compareDiscount"
                fill={COLORS.compare.discount}
                radius={[8, 8, 0, 0]}
                stackId="compare"
              />
              <Bar
                dataKey="compareFees"
                fill={COLORS.compare.fees}
                radius={[8, 8, 0, 0]}
                stackId="compare"
              >
                <LabelList
                  dataKey="totalCompare"
                  position="top"
                  formatter={(value) => USDollar.format(value)}
                  fontSize={10}
                  fill="#111827"
                />
              </Bar>
            </>
          )}
        </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
